import styled from 'styled-components'
import { H, Wrapper } from '@farewill/ui'
import { BORDER, COLOR, FONT, GTR } from '@farewill/ui/tokens'

import Badge from 'components/styled/badge'
import { FuneralPlanVersion } from 'lib/models/funeral-plan'

import {
  agreementBadge,
  ChangeBadge,
  formatDay,
  formatPaymentDay,
  formatPence,
} from './payment-shared'

const StyledHistory = styled(Wrapper)`
  padding: ${GTR.M};
  background: ${COLOR.WHITE};
  border: 1px solid ${COLOR.GREY.LIGHT};
  border-radius: ${BORDER.RADIUS.L};
`

const StyledQuote = styled.section`
  display: flex;
  flex-direction: column;
  margin-top: ${GTR.M};
  padding: ${GTR.S};
  background: ${COLOR.BACKGROUND.FOG};
  border-radius: ${BORDER.RADIUS.S};
  color: ${COLOR.GREY.DARK};
  font-size: ${FONT.SIZE.S};
`

const StyledDate = styled.div`
  margin-bottom: ${GTR.S};
`

const StyledName = styled.p`
  margin: 0 0 ${GTR.S};
  color: ${COLOR.BLACK};
  font-weight: ${FONT.WEIGHT.BOLD};
`

const StyledRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr;
  gap: ${GTR.S};
  align-items: start;
  padding: ${GTR.S} 0;
  border-bottom: 1px solid ${COLOR.GREY.LIGHT};
`

/** The amount, and the payment date, sit against the right edge */
const StyledRight = styled.span`
  text-align: right;
  white-space: nowrap;
`

const StyledHeadings = styled(StyledRow)`
  padding-top: 0;
  color: ${COLOR.BLACK};
  font-size: ${FONT.SIZE.M};
  font-weight: ${FONT.WEIGHT.BOLD};
`

/** A row with no rule under it when it is the last in the agreement */
const StyledLastRow = styled(StyledRow)`
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const StyledStrongRow = styled(StyledLastRow)`
  font-weight: ${FONT.WEIGHT.BOLD};
  color: ${COLOR.BLACK};
`

/** Set off from the plan's lines above by a darker rule */
const StyledInstalments = styled(StyledStrongRow)`
  border-top: 1px solid ${COLOR.GREY.MEDIUM};
`

type Props = {
  /** Every agreement on the plan, oldest first */
  versions: FuneralPlanVersion[]
}

/**
 * Every agreement on the plan, newest first (2.14): what it did, every line
 * the plan had once it was made, marking what it added or removed, then the
 * instalments in place and their payment date, or a single payment. A
 * cancellation shows everything given back, and the refund.
 */
const PaymentQuoteHistory = ({ versions }: Props): React.ReactElement => {
  /** The instalments the plan pays once an agreement is made: an instalment
   * agreement's own, or for something bought outright, the plan's as they
   * stood then */
  const instalmentsOf = ({
    type,
    payments,
    instalmentsInPlace,
  }: FuneralPlanVersion) =>
    type === 'Instalments' ? payments : (instalmentsInPlace ?? undefined)

  return (
    <StyledHistory>
      <H tag="h3" size="S" margin={0}>
        Agreement history
      </H>

      {[...versions].reverse().map((version) => {
        const {
          agreement,
          number,
          name,
          historyQuote,
          cancels,
          stillToPayInPence,
          payments,
        } = version
        const badge = agreementBadge(version)
        const instalments = cancels ? undefined : instalmentsOf(version)
        const singlePayment =
          !cancels && !instalments && number === 1 ? payments : undefined

        return (
          <StyledQuote key={agreement.id} data-testid="agreement-quote">
            <StyledDate>{formatDay(agreement.createdAt)}</StyledDate>
            {number > 1 && <StyledName>{name}</StyledName>}
            <StyledHeadings>
              <span>
                Agreement {number}
                {badge && (
                  <>
                    {' '}
                    <Badge $state={badge.state}>{badge.label}</Badge>
                  </>
                )}
              </span>
              <span>Units</span>
              <span>Changed</span>
              <StyledRight>Amount</StyledRight>
            </StyledHeadings>
            {historyQuote.map((line, lineIndex) => (
              <StyledLastRow key={lineIndex} data-testid="quote-row">
                <span>{line.description}</span>
                <span>{line.units}</span>
                <span>
                  <ChangeBadge change={line.change} />
                </span>
                <StyledRight>{formatPence(line.amountInPence)}</StyledRight>
              </StyledLastRow>
            ))}
            {cancels && (
              <StyledStrongRow data-testid="quote-row">
                <span>Cancellation refund</span>
                <span />
                <span />
                <StyledRight>
                  {formatPence(Math.abs(stillToPayInPence))}
                </StyledRight>
              </StyledStrongRow>
            )}
            {instalments && (
              <>
                <StyledInstalments data-testid="quote-row">
                  <span>Instalments</span>
                  <span>{instalments.count}</span>
                  <span />
                  <StyledRight>
                    {formatPence(instalments.amountInPence)}
                  </StyledRight>
                </StyledInstalments>
                <StyledStrongRow data-testid="quote-row">
                  <span>Payment date</span>
                  <span />
                  <span>
                    {version.paymentDateChanged && (
                      <ChangeBadge change="changed" />
                    )}
                  </span>
                  <StyledRight>
                    {instalments.count > 1 ? (
                      <>
                        {formatPaymentDay(instalments.startsOn)},
                        <br />
                        from {formatDay(instalments.startsOn)}
                      </>
                    ) : (
                      formatDay(instalments.startsOn)
                    )}
                  </StyledRight>
                </StyledStrongRow>
              </>
            )}
            {singlePayment && (
              <StyledLastRow data-testid="quote-row">
                <span>Single payment</span>
                <span>{singlePayment.count}</span>
                <span />
                <StyledRight>
                  {formatPence(singlePayment.amountInPence)}
                </StyledRight>
              </StyledLastRow>
            )}
          </StyledQuote>
        )
      })}
    </StyledHistory>
  )
}

export default PaymentQuoteHistory
